import React from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useMeterList } from "@features/mx/manufacturer/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Eye, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ResourcesProps {
  modelId: string;
}

export const ResourcesTab: React.FC<ResourcesProps> = ({ modelId }) => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState("");

  const { data: response, isLoading } = useMeterList({
    remote_utility_id: remoteUtilityId,
    meter_model_id: modelId,
    search_data: searchQuery,
  });

  const tableData = React.useMemo(() => {
    if (!response?.results || response.results.length === 0) {
      return [];
    }
    return response.results.map((r: any) => ({
      id: r.meterNumber || "NA",
      deviceNo: r.deviceNo || "NA",
      type: r.resourceTypeDisplay || "NA",
      subType: r.resourceSubtypeDisplay || "NA",
      utilityService: r.productCode || "NA",
      classification: r.ownershipDisplay || "NA",
      physicalStatus: r.physicalStatus || "NA",
      assignmentStatus: r.status || "NA",
    }));
  }, [response]);

  const columns: TableColumn<any>[] = [
    { key: "id", header: "Resource Number", sortable: true },
    { key: "deviceNo", header: "Serial No", sortable: true },
    { key: "type", header: "Type", sortable: true },
    { key: "subType", header: "Sub Type", sortable: true },
    { key: "utilityService", header: "Utility Service", sortable: true },
    { key: "classification", header: "Classification", sortable: true },
    {
      key: "physicalStatus",
      header: "Physical Status",
      sortable: true,
      render: (val: string) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${val === "Active"
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-50 text-gray-600 border-gray-200"
          }`}>
          {val}
        </span>
      ),
    },
    {
      key: "assignmentStatus",
      header: "Assignment Status",
      sortable: true,
      render: (val: string) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${val === "Assigned"
          ? "bg-blue-50 text-blue-700 border-blue-200"
          : "bg-gray-50 text-gray-600 border-gray-200"
          }`}>
          {val}
        </span>
      ),
    },
  ];

  const handleEdit = (row: any) => {
    const isWarehouse = row?.physicalStatus === "In Warehouse" || row?.physicalStatusLabel === "In Warehouse";
    const pathType = isWarehouse ? "warehouse" : "installed";
    navigate(`/mx/manage/resources/edit/${pathType}/${row.id}`);
  };

  const actions = [
    {
      label: "View",
      icon: <Eye className="w-4 h-4 text-gray-500" />,
      onClick: (row: any) => navigate(`/mx/manage/resources/view/${row?.id}`),
    },
    // {
    //   label: "Edit",
    //   icon: <Pencil className="w-4 h-4 text-gray-500" />,
    //   onClick: (row: any) => handleEdit,
    // },
  ];

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-150 shadow-sm space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-md font-bold text-gray-900">Associated Resources</h3>
      </div>
      <div className="border border-gray-150 rounded-xl overflow-hidden p-4">
        <GenericTable
          columns={columns}
          data={tableData}
          rowKey="id"
          loading={isLoading}
          actions={actions}
          actionsType="icons"
          searchPlaceholder="Search resources..."
          onSearch={(val) => setSearchQuery(val)}
        />
      </div>
    </div>
  );
};

export default ResourcesTab;
